import { useState, useEffect, useCallback } from "react";
import {
  Icon,
  apiFetch,
  apiPost,
  apiPatch,
  fmt,
  Loader,
  ini,
  avaClass,
  TECH_API,
} from "./apihandler";

function PaymentPage({ camp, onBack }) {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [paid, setPaid] = useState({});
  const [toast, setToast] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  const load = useCallback(() => {
    setLoading(true);

    Promise.all([
      apiFetch(`${TECH_API}/payment-settlements/?camp_id=${camp.id}`),
      apiFetch(`${TECH_API}/camp/${camp.id}/assignments/`),
    ])
      .then(([settlementData, assignmentData]) => {
        setSettlements(
          Array.isArray(settlementData)
            ? settlementData
            : settlementData.results || []
        );

        const technicianServiceRows =
          (Array.isArray(assignmentData)
            ? assignmentData
            : assignmentData.results || []
          ).map((a) => ({
            id: a.id,
            technicianId: a.technician,
            technicianName: a.technician_name,
            serviceId: a.service,
            serviceName: a.service_name,
          }));

        setTechnicians(technicianServiceRows);

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [camp.id]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async (technicianId, serviceId) => {
    setGeneratingId(
      `${technicianId}-${serviceId}`
    );

    try {
      await apiPost(`${TECH_API}/generate-settlement/`, {
        technician_id: technicianId,
        camp_id: camp.id,
        service_id: serviceId,
      });

      setToast("Settlement generated ✓");
      await load();
    } catch (err) {
      setToast("Generation failed");
    } finally {
      setGeneratingId(null);
    }
  };

  const confirmPaid = async (s) => {
    try {
      await apiPatch(
        `${TECH_API}/payment-settlements/${s.id}/`,
        { status: "PAID" }
      );

      setPaid((p) => ({ ...p, [s.id]: true }));
      setToast("Marked as paid ✓");

      setTimeout(() => setToast(null), 2500);
      load();
    } catch {
      setToast("Failed to update status");
    }
  };

  return (
    <div className="page">
      <div className="ph">
        <Icon n="receipt" size={18} />
        {camp.name || `Camp #${camp.id}`}
        <span className="ph-sub">— payment settlement</span>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "var(--text)",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 13,
            zIndex: 999,
          }}
        >
          {toast}
        </div>
      )}

      {loading && <Loader text="Loading settlements..." />}

      {/* EMPTY STATE */}
      {!loading && (
        <div className="card">
          <h4>Generate Settlements</h4>

          {technicians.length === 0 ? (
            <div>No technicians assigned to this camp.</div>
          ) : (
            technicians.map((t) => {
              const existingSettlement = settlements.find(
                (s) =>
                  (s.technician_id || s.technician) === t.technicianId &&
                  s.service === t.serviceId
              );

              return (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "10px 0",
                  }}
                >
                  <span>
                    {t.technicianName}
                    {" - "}
                    {t.serviceName}
                  </span>

                  <button
                    className="btn btn-pri"
                    onClick={() => generate(t.technicianId, t.serviceId)}
                    disabled={
                      generatingId ===
                        `${t.technicianId}-${t.serviceId}` ||
                      !!existingSettlement
                    }
                  >
                    {existingSettlement
                      ? "Generated"
                      : generatingId === `${t.technicianId}-${t.serviceId}`
                      ? "Generating..."
                      : "Generate"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* SETTLEMENT LIST */}
      {!loading &&
        settlements.map((s, i) => {
          const techName =
            s.technician_name ||
            `Technician #${s.technician || i}`;

          const isPaid =
            paid[s.id] ||
            (s.status || "").toUpperCase() === "PAID";

          const earnings = s.earnings_breakdown;
          const expenses = s.expense_breakdown || [];
          const advances = s.advance_breakdown || [];
          const summary = s.summary || {};

          return (
            <div className="card" key={s.id}>
              {/* HEADER */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: "1rem",
                  paddingBottom: ".9rem",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div className={`ava ${avaClass(i)}`}>
                  {ini(techName)}
                </div>

                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {techName}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text2)"
                    }}
                  >
                    Service: {s.service_name}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text2)" }}>
                    Generated on{" "}
                    {new Date(s.generated_at).toLocaleDateString()}
                  </div>
                </div>

                <span className="pill pill-blue" style={{ marginLeft: "auto" }}>
                  Settlement
                </span>
              </div>

              {/* 💰 EARNINGS */}
              {earnings && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Earnings
                  </div>

                  <div className="settle-row">
                    <span style={{ color: "var(--text2)" }}>
                      Cases × Rate
                    </span>
                    <span>{earnings.formula}</span>
                  </div>

                  <div className="settle-row settle-total">
                  <span>Gross Payment</span>
                  <span style={{ color: "var(--blue)" }}>
                    ₹{fmt(earnings.gross_payment)}
                  </span>
                </div>

                {/* ADD THIS */}
                {summary.incentive > 0 && (
                  <div className="settle-row">
                    <span style={{ color: "var(--text2)" }}>Incentive</span>
                    <span style={{ color: "var(--green)" }}>
                      + ₹{fmt(summary.incentive)}
                    </span>
                  </div>
                )}
                </>
              )}

              {/* 🧾 EXPENSES */}
              {expenses.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, marginTop: 10 }}>
                    Expenses
                  </div>

                  {expenses.map((e, idx) => (
                    <div className="settle-row" key={idx}>
                      <span style={{ color: "var(--text2)" }}>
                        {e.label} ({e.category})
                      </span>
                      <span style={{ color: "var(--red)" }}>
                        - ₹{fmt(e.amount)}
                      </span>
                    </div>
                  ))}
                </>
              )}

              {/* 💸 ADVANCES */}
              {advances.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, marginTop: 10 }}>
                    Advances
                  </div>

                  {advances.map((a, idx) => (
                    <div className="settle-row" key={idx}>
                      <span style={{ color: "var(--text2)" }}>
                        {a.label} · {a.mode}
                      </span>
                      <span style={{ color: "var(--red)" }}>
                        - ₹{fmt(a.amount)}
                      </span>
                    </div>
                  ))}
                </>
              )}

              {/* 🧮 SUMMARY */}
              <div className="settle-row settle-total" style={{ marginTop: 10 }}>
                <span>Net Payable</span>
                <span style={{ color: "var(--blue)", fontWeight: 600 }}>
                  ₹{fmt(summary.net_payable || s.final_payment)}
                </span>
              </div>

              {/* ACTIONS */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: ".9rem",
                  paddingTop: ".75rem",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <span className={`pill ${isPaid ? "pill-green" : "pill-gray"}`}>
                  {isPaid ? (
                    <>
                      <Icon n="check" size={11} /> Paid
                    </>
                  ) : (
                    <>
                      <Icon n="clock" size={11} /> {s.status || "DRAFT"}
                    </>
                  )}
                </span>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      generate(
                        s.technician_id || s.technician,
                        s.service
                      )
                    }
                    disabled={
                      generatingId ===
                      `${s.technician_id || s.technician}-${s.service}`
                    }
                  >
                    <Icon n="refresh" size={13} />
                    {generatingId ===
                    `${s.technician_id || s.technician}-${s.service}`
                      ? "Generating..."
                      : "Generate"}
                  </button>

                  {!isPaid && (
                    <button
                      className="btn btn-sm btn-pri"
                      onClick={() => confirmPaid(s)}
                    >
                      <Icon n="check" size={13} />
                      Confirm & Mark Paid
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

      <div className="btn-row">
        <button className="btn" onClick={onBack}>
          <Icon n="arrowL" size={15} />
          Back
        </button>
      </div>
    </div>
  );
}

export default PaymentPage;